import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lightning, PaperPlaneTilt, Robot, User, Sparkle, 
  CheckCircle, ArrowRight, Download, Calendar
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { aiAPI } from '../lib/api';
import { ScheduleExpertButton } from '../components/CalendlyModal';
import { SERVICES, INDUSTRIES, TECH_STACK, PRICING_MODELS } from '../data/siteData';
import { toast } from 'sonner';

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: `Hello! I'm the XceedAgents AI assistant. I'll help you scope your project, suggest the right tech stack, and provide a preliminary estimate.

**Let's start with the basics:**
What type of project are you looking to build?

1. MVP / Startup Product
2. Mobile Application
3. Web Application / SaaS
4. Enterprise System
5. AI/ML Integration
6. Something else`
};

const QUESTIONS = [
  {
    id: 'project_type',
    question: 'What type of project?',
    options: ['MVP / Startup', 'Mobile App', 'Web App / SaaS', 'Enterprise System', 'AI/ML Integration', 'Other']
  },
  {
    id: 'industry',
    question: 'What industry is this for?',
    options: INDUSTRIES.map(i => i.name)
  },
  {
    id: 'features',
    question: 'What are the key features you need?',
    freeform: true
  },
  {
    id: 'tech_preference',
    question: 'Do you have tech stack preferences?',
    options: ['React/Next.js', 'Vue/Nuxt', 'React Native', 'Flutter', 'Python/Django', 'Node.js', 'No preference']
  },
  {
    id: 'timeline',
    question: "What's your ideal timeline?",
    options: ['1-2 weeks (Rapid MVP)', '2-4 weeks', '1-2 months', '3+ months', 'Flexible']
  },
  {
    id: 'budget',
    question: "What's your budget range?",
    options: ['$5K - $15K', '$15K - $50K', '$50K - $100K', '$100K+', 'Not sure yet']
  },
  {
    id: 'contact_name',
    question: "What's your name?",
    freeform: true
  },
  {
    id: 'contact_email',
    question: "What's your email so we can send you the proposal?",
    freeform: true
  }
];

export default function StartProjectPage() {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [projectData, setProjectData] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [showProposal, setShowProposal] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [proposalText, setProposalText] = useState('');
  const [leadId, setLeadId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (role, content) => {
    setMessages(prev => [...prev, { role, content }]);
  };

  const handleOptionSelect = async (option) => {
    const currentQuestion = QUESTIONS[currentStep];
    
    addMessage('user', option);
    
    setProjectData(prev => ({
      ...prev,
      [currentQuestion.id]: option
    }));

    setIsTyping(true);
    
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const nextStep = currentStep + 1;
    
    if (nextStep < QUESTIONS.length) {
      const nextQuestion = QUESTIONS[nextStep];
      let response = `${getAcknowledgment(currentQuestion.id, option)}\n\n**${nextQuestion.question}**`;
      
      if (nextQuestion.options) {
        response += '\n\n' + nextQuestion.options.map((opt, i) => `${i + 1}. ${opt}`).join('\n');
      } else if (nextQuestion.freeform) {
        if (nextQuestion.id === 'contact_name') {
          response += '\n\nPlease enter your full name.';
        } else if (nextQuestion.id === 'contact_email') {
          response += '\n\nPlease enter your email address.';
        } else {
          response += '\n\nPlease describe the key features and functionality you need.';
        }
      }
      
      addMessage('assistant', response);
      setCurrentStep(nextStep);
    } else {
      // All questions answered — generate proposal
      const allData = { ...projectData, [currentQuestion.id]: option };
      await generateProposal(allData);
    }
    
    setIsTyping(false);
  };

  const generateProposal = async (data) => {
    setIsGenerating(true);
    addMessage('assistant', `Excellent! I have all the information I need. Let me analyze your requirements and generate a full proposal...

**Analyzing requirements...**
**Determining optimal tech stack...**
**Calculating timeline and budget...**
**Generating your proposal...**

This may take a moment.`);

    try {
      const response = await aiAPI.evaluate({
        name: data.contact_name || 'Website Visitor',
        email: data.contact_email || 'visitor@xceedagents.ai',
        company: '',
        country: 'US',
        description: `Project Type: ${data.project_type}\nIndustry: ${data.industry}\nFeatures: ${data.features || 'Standard features'}\nTech Preference: ${data.tech_preference}\nTimeline: ${data.timeline}\nBudget: ${data.budget}`,
        tech_stack: [data.tech_preference || 'React'],
        budget_range: data.budget,
        timeline: data.timeline,
        project_type: data.project_type,
        industry: data.industry,
        features: data.features || ''
      });
      
      const evalData = response.data.evaluation;
      const proposalContent = response.data.proposal || '';
      const savedLeadId = response.data.lead_id;
      
      setEvaluation(evalData);
      setProposalText(proposalContent);
      setLeadId(savedLeadId);
      setShowProposal(true);
      
      addMessage('assistant', `**Analysis Complete!**

I've prepared a detailed proposal based on your requirements. You can view the summary on the right and download the full proposal.

Your request has been saved — our team will review it and get back to you shortly.`);
    } catch (error) {
      console.error('Proposal generation error:', error);
      addMessage('assistant', `I've gathered your requirements! Our AI engine encountered a brief timeout while generating the full proposal — this happens with complex projects. 

Your information has been recorded. Our team will prepare a detailed proposal and reach out to you at **${data.contact_email}** within 24 hours.`);
      setShowProposal(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadProposal = async () => {
    if (!leadId) {
      toast.error('No proposal available yet. Complete the wizard first.');
      return;
    }
    try {
      toast.info('Generating your branded PDF proposal...');
      const response = await aiAPI.downloadProposalPDF(leadId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `XceedAgents-Proposal-${(projectData.contact_name || 'Project').replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Proposal PDF downloaded!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isTyping || isGenerating) return;

    const currentQuestion = QUESTIONS[currentStep];
    
    if (currentQuestion?.freeform) {
      handleOptionSelect(input);
    } else {
      const optionNum = parseInt(input);
      const options = currentQuestion?.options || QUESTIONS[0].options;
      if (optionNum >= 1 && optionNum <= options.length) {
        handleOptionSelect(options[optionNum - 1]);
      } else {
        handleOptionSelect(input);
      }
    }
    
    setInput('');
  };

  const getAcknowledgment = (questionId, answer) => {
    const acks = {
      project_type: `Great choice! ${answer} projects are one of our specialties.`,
      industry: `Excellent! We have extensive experience in the ${answer} sector.`,
      features: 'Those sound like great features!',
      tech_preference: `${answer} is a solid choice for your needs.`,
      timeline: 'We can work with that timeline.',
      budget: 'Got it, that helps us plan appropriately.',
      contact_name: `Nice to meet you, ${answer}!`,
      contact_email: `Perfect, we'll send the proposal to ${answer}.`,
    };
    return acks[questionId] || 'Got it!';
  };

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chat Section */}
          <div className="lg:col-span-2">
            <div className="border theme-border theme-surface h-[600px] flex flex-col">
              {/* Header */}
              <div className="p-4 border-b theme-border flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center">
                  <Robot size={24} className="text-black" />
                </div>
                <div>
                  <h3 className="font-bold">Project Consultant AI</h3>
                  <p className="text-xs theme-text-muted">Powered by XceedAgents</p>
                </div>
                <div className="ml-auto flex items-center gap-2 text-xs text-[#00FF94]">
                  <span className="w-2 h-2 rounded-full bg-[#00FF94]" />
                  Online
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center ${
                      msg.role === 'assistant' 
                        ? 'bg-[#00F0FF]/20' 
                        : 'bg-[#7000FF]/20'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <Robot size={18} className="text-[#00F0FF]" />
                      ) : (
                        <User size={18} className="text-[#7000FF]" />
                      )}
                    </div>
                    <div className={`max-w-[80%] p-4 ${
                      msg.role === 'assistant'
                        ? 'theme-surface-el border theme-border'
                        : 'bg-[#7000FF]/20 border border-[#7000FF]/30'
                    }`}>
                      <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                    </div>
                  </motion.div>
                ))}
                
                {(isTyping || isGenerating) && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-3"
                  >
                    <div className="w-8 h-8 bg-[#00F0FF]/20 flex items-center justify-center">
                      <Robot size={18} className="text-[#00F0FF]" />
                    </div>
                    <div className="theme-surface-el border theme-border p-4">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-[#00F0FF] animate-bounce" />
                        <span className="w-2 h-2 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <span className="w-2 h-2 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Options */}
              {!showProposal && currentStep < QUESTIONS.length && QUESTIONS[currentStep]?.options && (
                <div className="p-4 border-t theme-border">
                  <div className="flex flex-wrap gap-2">
                    {QUESTIONS[currentStep].options.map((option, i) => (
                      <button
                        key={i}
                        onClick={() => handleOptionSelect(option)}
                        disabled={isTyping || isGenerating}
                        data-testid={`chat-option-${i}`}
                        className="px-3 py-2 text-sm border border-white/20 hover:border-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:opacity-50"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <form onSubmit={handleSubmit} className="p-4 border-t theme-border">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={showProposal ? "Proposal generated! Check the sidebar." : "Type your message..."}
                    className="flex-1 bg-[var(--input-bg)] border theme-border px-4 py-3 text-sm focus:border-[#00F0FF] focus:outline-none"
                    data-testid="chat-input"
                    disabled={isGenerating}
                  />
                  <Button 
                    type="submit"
                    className="bg-[#00F0FF] text-black px-4"
                    disabled={isTyping || isGenerating}
                    data-testid="chat-send"
                  >
                    <PaperPlaneTilt size={20} />
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar - Progress & Proposal */}
          <div className="space-y-6">
            {/* Progress */}
            <div className="border theme-border theme-surface p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Sparkle size={20} className="text-[#00F0FF]" />
                Progress
              </h3>
              <div className="space-y-3">
                {QUESTIONS.map((q, i) => (
                  <div key={q.id} className="flex items-center gap-3">
                    <div className={`w-6 h-6 flex items-center justify-center text-xs ${
                      i < currentStep 
                        ? 'bg-[#00FF94] text-black' 
                        : i === currentStep 
                          ? 'bg-[#00F0FF] text-black'
                          : 'bg-white/10 theme-text-muted'
                    }`}>
                      {i < currentStep ? <CheckCircle size={14} /> : i + 1}
                    </div>
                    <span className={`text-sm ${i <= currentStep ? 'text-white' : 'theme-text-muted'}`}>
                      {q.question}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Proposal Preview */}
            <AnimatePresence>
              {showProposal && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-[#00F0FF]/50 theme-surface p-6"
                  data-testid="proposal-card"
                >
                  <h3 className="font-bold mb-4 flex items-center gap-2 text-[#00F0FF]">
                    <Lightning size={20} />
                    Project Proposal
                  </h3>

                  {evaluation && !evaluation.error ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Recommended Stack</p>
                        <div className="flex flex-wrap gap-2">
                          {(evaluation.recommended_stack || ['React', 'Node.js', 'MongoDB']).slice(0, 5).map((tech, i) => (
                            <span key={i} className="px-2 py-1 text-xs bg-white/10">{tech}</span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Timeline</p>
                        <p className="text-2xl font-bold text-[#00F0FF]">
                          {evaluation.estimated_timeline_weeks || '4-6'} weeks
                        </p>
                      </div>

                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Budget Estimate</p>
                        <p className="text-2xl font-bold text-[#00FF94]">
                          ${(evaluation.estimated_budget_usd?.min || 10000).toLocaleString()} - ${(evaluation.estimated_budget_usd?.max || 30000).toLocaleString()}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Complexity</p>
                        <span className="px-3 py-1 text-sm bg-[#7000FF]/20 text-[#7000FF] capitalize">
                          {evaluation.complexity || 'Medium'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Project Type</p>
                        <p className="font-bold">{projectData.project_type}</p>
                      </div>
                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Industry</p>
                        <p className="font-bold">{projectData.industry}</p>
                      </div>
                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Timeline</p>
                        <p className="font-bold">{projectData.timeline}</p>
                      </div>
                      <div>
                        <p className="text-xs theme-text-muted uppercase mb-1">Budget</p>
                        <p className="font-bold">{projectData.budget}</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 space-y-3">
                    <ScheduleExpertButton
                      className="w-full bg-[#00F0FF] text-black font-bold h-12 rounded-md inline-flex items-center justify-center"
                      testId="schedule-call-btn"
                    >
                      <Calendar size={18} className="mr-2" />
                      Schedule a meeting with our Expert
                    </ScheduleExpertButton>
                    <Button 
                      variant="outline" 
                      className="w-full border-white/20 h-12"
                      onClick={handleDownloadProposal}
                      data-testid="download-proposal-btn"
                    >
                      <Download size={18} className="mr-2" />
                      Download Proposal
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Contact Info */}
            <div className="border theme-border theme-surface p-6">
              <h3 className="font-bold mb-4">Prefer to Talk?</h3>
              <p className="text-sm theme-text-muted mb-4">
                Schedule a call with our team for a personalized consultation.
              </p>
              <ScheduleExpertButton
                className="w-full h-10 rounded-md border border-white/20 font-medium text-sm inline-flex items-center justify-center hover:bg-white/5 transition-colors"
                testId="book-meeting-btn"
              >
                Book a Meeting
              </ScheduleExpertButton>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
